// content-grounding.js — pins the content generator's PLACE/INFRASTRUCTURE
// statements to system data (2026-07-08).
//
// Incident: a fresh regeneration of route antiparos-athens claimed the flight
// departs from "Antiparos Airport, the island's main airfield" — Antiparos has
// NO airport. Root cause: the route generator metadata carried no pad data at
// all, so the prompt said "Departure: main airport. Arrival: main airport."
// and the model invented plausible infrastructure.
//
// Owner rule (binding): the generator uses ONLY system data for places and
// infrastructure — no research, no plausibility guesses. Two defence lines:
//   1. buildLocationFacts()          — a structured FACTS block in the prompt,
//      naming the exact SERVED landing sites (canonical served gate — the
//      same buildPadServedPredicate the customer landing-fact sentences use)
//      plus explicit NEGATIVE facts ("X has NO airport") where relevant.
//   2. findUnverifiedLocationClaims() — deterministic post-generation check;
//      any "{City} Airport"/"{City} Airfield"-class claim for a city with no
//      airport-type location in the system REJECTS the output BEFORE the
//      Sanity write (no silent pass, no auto-rewrite — the admin regenerates).
//
// False-positive care: real airports (Athens International Airport) are fine —
// matching runs against the SYSTEM location list, never against word bans.
import { buildPadServedPredicate } from './pad-visibility.js';

const norm = (s) => String(s || '').trim().toLowerCase();

// City → infrastructure truth over the RAW location docs. `hasAirport` is
// infrastructure existence (any airport-type location doc for that city,
// enabled or not) — the negative fact must reflect reality, not visibility.
export function buildCityAirportIndex(locations) {
  const index = new Map(); // norm(city) → { city, hasAirport }
  for (const l of (locations || [])) {
    const key = norm(l?.city);
    if (!key) continue;
    const cur = index.get(key) || { city: String(l.city).trim(), hasAirport: false };
    if (l.type === 'airport') cur.hasAirport = true;
    index.set(key, cur);
  }
  return index;
}

// SERVED landing sites for one city — enabled + served by ≥1 active operator
// (the canonical gate), airports first, deduped by name.
export function servedPadsForCity(cityName, locations, operators) {
  const served = buildPadServedPredicate(operators);
  const key = norm(cityName);
  const seen = new Set();
  return (locations || [])
    .filter(l => l && norm(l.city) === key && l.enabled !== false
      && served({ _id: l._id, operatorIds: l.operatorIds || [], adminAssignedOnly: !!l.adminAssignedOnly }))
    .map(l => ({ name: l.name, type: l.type === 'airport' ? 'airport' : 'helipad' }))
    .filter(p => p.name && !seen.has(norm(p.name)) && seen.add(norm(p.name)))
    .sort((a, b) => (a.type === 'airport' ? 0 : 1) - (b.type === 'airport' ? 0 : 1)
      || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

// The structured FACTS block injected into the generation prompt. `roles`
// labels each city (e.g. ['Departure', 'Arrival'] for a route; ['Landing']
// for a destination/hotel city). Returns '' when no city resolves.
export function buildLocationFacts(cityNames, locations, operators, roles = []) {
  const airportIndex = buildCityAirportIndex(locations);
  const parts = [];
  (cityNames || []).forEach((cityName, i) => {
    if (!cityName) return;
    const pads = servedPadsForCity(cityName, locations, operators);
    const role = roles[i] || 'Landing';
    const info = airportIndex.get(norm(cityName));
    const padList = pads.length
      ? pads.map(p => p.name + ' (' + p.type + ')').join(', ')
      : 'no bookable landing site listed';
    let line = role + ' points for ' + cityName + ': ' + padList + '.';
    if (info && !info.hasAirport) {
      line += ' ' + cityName + ' has NO airport — never call any ' + cityName + ' landing site an airport or airfield.';
    }
    parts.push(line);
  });
  if (!parts.length) return '';
  return 'LOCATION FACTS (system data — the ONLY source of truth for places and infrastructure): '
    + parts.join(' ')
    + ' Use EXACTLY these names and types; if a location detail is not listed here, do not claim it. ';
}

// Deterministic post-generation check. Walks every string in the parsed
// content object and flags "{City} Airport"/"{City} International/Municipal/
// National Airport"/"{City} Airfield" claims for cities that have NO
// airport-type location in the system. Extensible rule list by design.
export function findUnverifiedLocationClaims(content, locations) {
  const airportIndex = buildCityAirportIndex(locations);
  const noAirportCities = [...airportIndex.values()].filter(c => !c.hasAirport && c.city.length >= 3);
  const violations = [];
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rules = noAirportCities.map(c => ({
    city: c.city,
    re: new RegExp('\\b' + esc(c.city) + "(?:'s)?\\s+(?:international\\s+|municipal\\s+|national\\s+)?air(?:port|field)\\b", 'i'),
  }));
  const walk = (v, field) => {
    if (typeof v === 'string') {
      for (const r of rules) {
        const m = v.match(r.re);
        if (m) violations.push({ field, city: r.city, claim: m[0] });
      }
    } else if (Array.isArray(v)) {
      v.forEach((x, i) => walk(x, field + '[' + i + ']'));
    } else if (v && typeof v === 'object') {
      for (const [k, val] of Object.entries(v)) walk(val, field ? field + '.' + k : k);
    }
  };
  walk(content, '');
  return violations;
}
